/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import anime from 'animejs';
import { logger } from '../utils/logger';

const BeforeAfterSlider = ({ showDashboard, onToggleDashboard }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);

  // Your actual before/after haircut transformation images
  const beforeImage = "/images/before-after/slider_left.png"; // Your "before" image
  const afterImage = "/images/before-after/slider_right.png"; // Your "after" image

  // Fallback images (external) - backup in case local images fail to load
  const beforeImageFallback = "https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=600&h=800&fit=crop&crop=face";
  const afterImageFallback = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=800&fit=crop&crop=face";

  // Enhanced dashboard animations
  useEffect(() => {
    const dashboardElement = document.querySelector('.floating-dashboard');
    
    if (showDashboard && dashboardElement) {
      logger.debug('Showing dashboard with animation');
      
      // Entrance animation
      anime({
        targets: dashboardElement,
        translateY: ['-100px', '0px'],
        opacity: [0, 1],
        scale: [0.7, 1],
        rotateX: ['-20deg', '0deg'],
        duration: 700,
        easing: 'easeOutBack(1.7)',
        complete: () => {
          // Animate metrics with stagger effect
          anime({
            targets: '.dashboard-metric',
            translateX: [-50, 0],
            opacity: [0, 1],
            duration: 400,
            delay: anime.stagger(150),
            easing: 'easeOutQuart'
          });
          
          // Animate progress bars
          anime({
            targets: '.progress-bar',
            width: ['0%', '100%'],
            duration: 800,
            delay: anime.stagger(200, {start: 300}),
            easing: 'easeOutExpo'
          });
        }
      });
      
    } else if (!showDashboard && dashboardElement) {
      logger.debug('Hiding dashboard with animation');
      
      // Exit animation
      anime({
        targets: dashboardElement,
        translateY: ['0px', '-100px'],
        opacity: [1, 0],
        scale: [1, 0.8],
        rotateX: ['0deg', '20deg'],
        duration: 400,
        easing: 'easeInBack(1.7)'
      });
    }
  }, [showDashboard]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    updateSliderPosition(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    updateSliderPosition(e);
  };

  const handleMouseUp = (e) => {
    if (e) {
      e.preventDefault();
    }
    setIsDragging(false);
  };

  const updateSliderPosition = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleTouchStart = (e) => {
    // Only prevent default for the container, not globally
    e.preventDefault();
    setIsDragging(true);

    // Prevent body scroll during drag
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    updateSliderPositionTouch(e);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    updateSliderPositionTouch(e);
  };

  const handleTouchEnd = (e) => {
    if (e) {
      e.preventDefault();
    }

    setIsDragging(false);

    // Restore body scroll
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  };

  const updateSliderPositionTouch = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => {
        if (e.type === 'mousemove') {
          handleMouseMove(e);
        } else if (e.type === 'touchmove') {
          handleTouchMove(e);
        }
      };

      const handleEnd = (e) => {
        if (e.type === 'mouseup') {
          handleMouseUp(e);
        } else if (e.type === 'touchend' || e.type === 'touchcancel') {
          handleTouchEnd(e);
        }
      };

      // Add event listeners
      document.addEventListener('mousemove', handleMove, { passive: false });
      document.addEventListener('mouseup', handleEnd, { passive: false });
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd, { passive: false });
      document.addEventListener('touchcancel', handleEnd, { passive: false });

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
        document.removeEventListener('touchcancel', handleEnd);

        // Ensure body styles are reset on cleanup
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isDragging]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  return (
    <div
      className="w-full max-w-5xl mx-auto px-4 sm:px-0 relative"
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
    >
      {/* AI Dashboard - All Devices */}
      <div
        className={`floating-dashboard absolute top-4 left-1/2 transform -translate-x-1/2 z-30 ${
          showDashboard ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 p-4 sm:p-6 min-w-[280px] max-w-[320px] sm:min-w-[320px] sm:max-w-[400px]">
          {/* Dashboard Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-800">AI Analysis Dashboard</h3>
            </div>
            <button
              onClick={(e) => {
                console.log('Close button clicked!'); // Debug log
                
                // Dramatic close animation
                anime({
                  targets: e.currentTarget,
                  rotate: [0, 360],
                  scale: [1, 1.3, 0.8, 1],
                  duration: 500,
                  easing: 'easeOutElastic(1, .6)',
                  complete: () => {
                    console.log('Close animation completed');
                  }
                });
                
                // Icon spin animation
                anime({
                  targets: e.currentTarget.querySelector('svg'),
                  rotateZ: [0, 180],
                  duration: 300,
                  easing: 'easeOutBack(1.7)'
                });
                
                // Delay close for animation
                setTimeout(() => {
                  onToggleDashboard && onToggleDashboard();
                }, 200);
              }}
              onMouseEnter={(e) => {
                anime({
                  targets: e.currentTarget,
                  scale: 1.2,
                  rotate: 15,
                  duration: 200,
                  easing: 'easeOutQuart'
                });
              }}
              onMouseLeave={(e) => {
                anime({
                  targets: e.currentTarget,
                  scale: 1,
                  rotate: 0,
                  duration: 200,
                  easing: 'easeOutQuart'
                });
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Analysis Description */}
          <p className="text-xs sm:text-sm text-gray-600 mb-4 leading-relaxed">
            Real-time facial geometry analysis with 99.7% accuracy
          </p>

          {/* Analysis Metrics */}
          <div className="space-y-3">
            <div className="dashboard-metric flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Face Analysis</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 sm:w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="progress-bar h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full" style={{ width: '99.7%' }}></div>
                </div>
                <span className="text-xs sm:text-sm font-bold text-amber-600">99.7%</span>
              </div>
            </div>

            <div className="dashboard-metric flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Style Matching</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 sm:w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="progress-bar h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full" style={{ width: '98.9%' }}></div>
                </div>
                <span className="text-xs sm:text-sm font-bold text-blue-600">98.9%</span>
              </div>
            </div>

            <div className="dashboard-metric flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Processing Speed</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 sm:w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="progress-bar h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full" style={{ width: '95%' }}></div>
                </div>
                <span className="text-xs sm:text-sm font-bold text-green-600">95%</span>
              </div>
            </div>
          </div>

          {/* Dashboard Footer */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Witness the dramatic transformation from unruly, longer hair to a sleek, professional cut. This demonstration showcases how our AI analyzes your features and recommends the perfect style.
            </p>
          </div>
        </div>

        {/* Floating Dashboard Pointer */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
          <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-white/95"></div>
        </div>
      </div>



      {/* Enhanced Main Slider Container */}
      <div
        ref={containerRef}
        className="relative w-full h-[300px] sm:h-[400px] md:h-[550px] lg:h-[650px] rounded-2xl sm:rounded-3xl overflow-hidden cursor-col-resize group shadow-2xl border-2 sm:border-4 border-yellow-500/30 transition-all duration-500 hover:border-yellow-400/50"
        style={{
          background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          touchAction: 'pan-y' // Allow vertical scroll but prevent horizontal gestures
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        unselectable="on"
      >
        {/* Enhanced Before Image */}
        <div className="absolute inset-0">
          <img
            src={beforeImage}
            alt="Before transformation"
            className="w-full h-full object-cover transition-all duration-500"
            style={{
              filter: isHovered ? 'brightness(0.9) contrast(1.1)' : 'brightness(0.85) contrast(1.05)',
              objectPosition: 'center 25%', // Focus on the face area
              userSelect: 'none',
              WebkitUserSelect: 'none',
              pointerEvents: 'none'
            }}
            draggable={false}
            onError={(e) => { e.target.src = beforeImageFallback }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent"></div>

          {/* Before Overlay Text */}
          <div className="absolute top-2 sm:top-4 left-3 sm:left-6 z-20" style={{ opacity: sliderPosition < 50 ? 1 : 0 }}>
            <div className="bg-black/70 backdrop-blur-xl rounded-lg sm:rounded-xl px-2 sm:px-4 py-1 sm:py-2 border border-gray-500/40">
              <p className="text-white text-xs sm:text-sm font-semibold uppercase tracking-wide">Original Style</p>
            </div>
          </div>
        </div>

        {/* Enhanced After Image */}
        <div
          className="absolute inset-0 transition-all duration-100 ease-out"
          style={{
            clipPath: `polygon(${sliderPosition}% 0%, 100% 0%, 100% 100%, ${sliderPosition}% 100%)`
          }}
        >
          <img
            src={afterImage}
            alt="After AI transformation"
            className="w-full h-full object-cover transition-all duration-500"
            style={{
              filter: isHovered ? 'brightness(1.1) contrast(1.15) saturate(1.1)' : 'brightness(1.05) contrast(1.1)',
              objectPosition: 'center 25%', // Focus on the face area
              userSelect: 'none',
              WebkitUserSelect: 'none',
              pointerEvents: 'none'
            }}
            draggable={false}
            onError={(e) => { e.target.src = afterImageFallback }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-yellow-400/10 to-yellow-400/20"></div>

          {/* After Overlay Text */}
          <div className="absolute top-2 sm:top-4 right-3 sm:right-6 z-20" style={{ opacity: sliderPosition >= 50 ? 1 : 0 }}>
            <div className="bg-gradient-to-r from-yellow-500/95 to-amber-600/95 backdrop-blur-xl rounded-lg sm:rounded-xl px-2 sm:px-4 py-1 sm:py-2 border border-yellow-400/60 shadow-lg">
              <p className="text-black text-xs sm:text-sm font-bold uppercase tracking-wide">AI Enhanced</p>
            </div>
          </div>
        </div>

        {/* Enhanced Slider Line with Glow */}
        <div
          className="absolute top-0 bottom-0 cursor-col-resize transition-all duration-100 z-20"
          style={{
            left: `${sliderPosition}%`,
            width: isHovered || isDragging ? '4px' : '3px',
            background: 'linear-gradient(180deg, #c9a038 0%, #ffffff 20%, #c9a038 40%, #7d6633 60%, #c9a038 80%, #ffffff 100%)',
            boxShadow: `0 0 30px rgba(201, 160, 56, ${isHovered || isDragging ? 1.0 : 0.8}), 0 0 60px rgba(201, 160, 56, 0.6), 0 0 100px rgba(201, 160, 56, 0.3)`
          }}
        >
          {/* Enhanced Slider Handle */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border-2 sm:border-4 cursor-col-resize transition-all duration-300 shadow-2xl"
            style={{
              width: isHovered || isDragging ? (window.innerWidth < 640 ? '50px' : '70px') : (window.innerWidth < 640 ? '45px' : '60px'),
              height: isHovered || isDragging ? (window.innerWidth < 640 ? '50px' : '70px') : (window.innerWidth < 640 ? '45px' : '60px'),
              background: 'linear-gradient(135deg, #c9a038 0%, #ffffff 25%, #c9a038 50%, #7d6633 75%, #c9a038 100%)',
              borderColor: 'white',
              boxShadow: `0 10px 40px rgba(201, 160, 56, 0.8), inset 0 2px 10px rgba(255, 255, 255, 0.3), 0 0 0 ${isHovered || isDragging ? '8px' : '4px'} rgba(201, 160, 56, 0.2)`
            }}
          >
            {/* Enhanced Handle Icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="text-white transition-all duration-300"
                width={isHovered || isDragging ? (window.innerWidth < 640 ? "24" : "32") : (window.innerWidth < 640 ? "20" : "28")}
                height={isHovered || isDragging ? (window.innerWidth < 640 ? "24" : "32") : (window.innerWidth < 640 ? "20" : "28")}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                  pointerEvents: 'none'
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
              </svg>
            </div>

            {/* Pulsing Ring Animation */}
            <div
              className="absolute inset-0 rounded-full border-2 border-white/50 animate-pulse"
              style={{
                animation: isDragging ? 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite' : 'none'
              }}
            ></div>
          </div>
        </div>

        {/* Enhanced Hover Instruction - Hidden on mobile for better UX */}
        <div
          className="hidden sm:block absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-xl rounded-xl px-4 py-2 border border-yellow-500/40 transition-all duration-500 shadow-2xl"
          style={{
            opacity: isHovered && !isDragging ? 1 : 0,
            transform: `translate(-50%, ${isHovered && !isDragging ? '0' : '10px'})`
          }}
        >
          <p className="text-white text-sm font-medium uppercase tracking-normal flex items-center">
            <svg className="w-4 h-4 mr-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4M7 4H17M7 4L6 22H18L17 4" />
            </svg>
            Drag to Reveal AI Magic
          </p>
        </div>

        {/* Mobile Touch Instruction */}
        <div
          className="sm:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-xl rounded-lg px-3 py-1.5 border border-yellow-500/40 transition-all duration-500 shadow-2xl"
          style={{
            opacity: !isDragging ? 1 : 0
          }}
        >
          <p className="text-white text-xs font-medium uppercase tracking-normal flex items-center">
            <svg className="w-3 h-3 mr-1.5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4M7 4H17M7 4L6 22H18L17 4" />
            </svg>
            Touch & Drag
          </p>
        </div>

        {/* Dynamic Luxury Glow Effects */}
        <div
          className="absolute inset-0 pointer-events-none transition-all duration-300"
          style={{
            background: `radial-gradient(circle at ${sliderPosition}% 50%, rgba(201, 160, 56, ${isHovered ? 0.4 : 0.2}) 0%, transparent 60%)`
          }}
        ></div>

        {/* Animated Particles Effect - Reduced on mobile for performance */}
        {isHovered && window.innerWidth >= 640 && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-pulse"
                style={{
                  background: 'radial-gradient(circle, #c9a038 0%, transparent 70%)',
                  left: `${sliderPosition + (Math.sin(Date.now() * 0.001 + i) * 10)}%`,
                  top: `${50 + (Math.cos(Date.now() * 0.001 + i) * 20)}%`,
                  animationDelay: `${i * 0.2}s`
                }}
              ></div>
            ))}
          </div>
        )}

        {/* Progress Indicators - Repositioned and Made Smaller */}
        <div className="absolute top-12 sm:top-16 left-1/2 transform -translate-x-1/2 z-5">
          <div className="bg-black/40 backdrop-blur-lg rounded-full px-2 sm:px-3 py-1 border border-yellow-500/20">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="text-yellow-400 text-xs font-medium">{Math.round(sliderPosition)}%</div>
              <div className="w-8 sm:w-12 h-0.5 bg-gray-600 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 transition-all duration-300 rounded-full"
                  style={{ width: `${sliderPosition}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default BeforeAfterSlider; 